import { type ReactNode } from 'react';
import { DossierTable, FieldProse, SubsectionHeading, obj } from './DossierPrimitives';

// 12 Design-and-Use questions — MUST stay identical to backend
// cover_letter._DESIGN_USE_QUESTIONS and the 510(k) EstarRenderer list
// (pinned by the cover-letter equivalence test).
const DESIGN_USE_QUESTIONS: [string, string][] = [
  ['Is the device intended for prescription use (21 CFR 801 Subpart D)?', 'prescription_use'],
  ['Is the device intended for over-the-counter use (21 CFR 807 Subpart C)?', 'otc_use'],
  ['Does the device contain components derived from a tissue or other biologic source?', 'biologic_source_components'],
  ['Is the device provided sterile?', 'provided_sterile'],
  ['Is the device intended for single use?', 'single_use'],
  ['Is the device a reprocessed single use device?', 'reprocessed_single_use'],
  ['If yes, does this device type require reprocessed validation data?', 'reprocessed_validation_required'],
  ['Does the device contain a drug?', 'contains_drug'],
  ['Does the device contain a biologic?', 'contains_biologic'],
  ['Does the device use software?', 'uses_software'],
  ['Does the submission include clinical information?', 'clinical_information_included'],
  ['Is the device implanted?', 'implanted'],
];

const FDA_ADDRESSEE =
  'Food and Drug Administration\nCenter for Devices and Radiological Health\n' +
  'Document Mail Center - WO66-G609\n10903 New Hampshire Avenue\n' +
  'Silver Spring, Maryland 20993-0002\nUnited States of America';

/**
 * Shared structured §2 cover-letter body for the De Novo and PMA renderers —
 * identical scaffolding to the 510(k) Section2 (FDA addressee → admin table →
 * reason → eSTAR → design-and-use table), parameterized per pathway. PMA passes
 * `extraStatements` (User Fee) and `usRepresentative`. Prefer-legacy for old
 * prose dossiers. Lockstep with backend cover_letter.render_structured_cover_letter.
 * The 510(k) Section2 keeps its own copy (frozen while its RA audit is pending).
 */
export function StructuredCoverLetter({
  s2,
  submissionTypeRowLabel,
  reasonHeading,
  reasonPlaceholder,
  estarStatement,
  extraStatements = [],
  usRepresentative = false,
}: {
  s2: Record<string, unknown>;
  submissionTypeRowLabel: string;
  reasonHeading: string;
  reasonPlaceholder: string;
  estarStatement: string;
  extraStatements?: [string, string][];
  usRepresentative?: boolean;
}): ReactNode {
  const admin = obj(s2, 'administrative_information');
  const body = typeof s2.body === 'string' ? s2.body : '';
  const hasProse = body.trim().length > 0 && !body.trim().startsWith('__PLACEHOLDER__');
  if (Object.keys(admin).length === 0 || hasProse) {
    return <FieldProse label="Cover letter:" body={s2.body} fallbackHint="Cover letter to FDA DCC" />;
  }
  const cc = obj(admin, 'company_contact');
  const sc = obj(admin, 'submission_contact');
  const trade = Array.isArray(admin.device_trade_names) ? (admin.device_trade_names as unknown[]) : [];
  const du = obj(s2, 'design_and_use');
  const usRep = obj(s2, 'us_authorized_representative');
  const adminRows: unknown[][] = [
    ['FDA Submission Document No.', admin.fda_submission_document_no],
    [submissionTypeRowLabel, admin.submission_type_label],
    ['Address', admin.sponsor_address],
    ['Company Contact Person', cc.name],
    ['Company Contact Phone', cc.phone],
    ['Company Contact Email', cc.email],
    ['Establishment Registration', admin.establishment_registration],
    ['Submission Contact Person', sc.name],
    ['Submission Contact Phone', sc.phone],
    ['Submission Contact Email', sc.email],
    ['Device Common Name', admin.device_common_name],
    ['Device Trade/Proprietary Name', trade.length ? trade.map(String).join('; ') : null],
    ['Continued Confidentiality', 'Request made for continued confidentiality.'],
    ['Classification Name', admin.classification_name],
    ['Classification Regulation', admin.classification_regulation],
    ['Device Classification', admin.device_class],
    ['Device Panel', admin.device_panel],
    ['Device Product Code', admin.device_product_code],
    ['Prior FDA Document Numbers', admin.prior_fda_document_numbers],
  ];
  const duRows: unknown[][] = DESIGN_USE_QUESTIONS.map(([q, k]) => [q, du[k]]);
  return (
    <>
      <SubsectionHeading number="" title="FDA Addressee" />
      <div className="text-[13.5px] whitespace-pre-line">{FDA_ADDRESSEE}</div>
      <SubsectionHeading number="" title="Administrative Information" />
      <DossierTable headers={['Field', 'Value']} rows={adminRows} />
      <SubsectionHeading number="" title={reasonHeading} />
      <FieldProse label="" body={s2.reason_for_submission} fallbackHint={reasonPlaceholder} />
      <SubsectionHeading number="" title="eSTAR Statement" />
      <div className="text-[13.5px]">{estarStatement}</div>
      {extraStatements.map(([h, t]) => (
        <div key={h}>
          <SubsectionHeading number="" title={h} />
          <div className="text-[13.5px]">{t}</div>
        </div>
      ))}
      <SubsectionHeading number="" title="Design and Use of the Device" />
      <DossierTable headers={['Question', 'Answer']} rows={duRows} />
      {usRepresentative && (
        <>
          <SubsectionHeading number="" title="U.S. Authorized Representative (21 CFR 814.20(a))" />
          <DossierTable
            headers={['Field', 'Value']}
            rows={[['Name', usRep.name], ['Title', usRep.title], ['Address', usRep.address]]}
          />
        </>
      )}
    </>
  );
}
